import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "./SessionProvider";
import { homePathForUser, isBlockedStatus, type AccountRole } from "./types";
import AccessBlocked from "./AccessBlocked";

interface ProtectedRouteProps {
  /** Rôles autorisés à accéder à cet espace. */
  allowedRoles: AccountRole[];
  children: ReactNode;
}

/** Écran de chargement affiché pendant la restauration de la session. */
function SessionLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50">
      <div className="flex flex-col items-center gap-3">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        <p className="text-sm text-foreground-500">Chargement de votre espace…</p>
      </div>
    </div>
  );
}

/**
 * Garde de routes par rôle (frontend + session Supabase).
 * - Pendant le chargement de la session : écran de chargement.
 * - Sans session : redirection vers /connexion.
 * - Compte en attente / suspendu / désactivé : écran de blocage.
 * - Mauvais espace : redirection vers l'espace autorisé du rôle.
 */
export default function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return <SessionLoading />;
  }

  if (!session) {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }

  if (isBlockedStatus(session.statut)) {
    return <AccessBlocked session={session} />;
  }

  if (!allowedRoles.includes(session.role)) {
    return <Navigate to={homePathForUser(session)} replace />;
  }

  return <>{children}</>;
}